import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Store, Search, TrendingUp, Clock, Star, Download, Share2, Users, Crown, Check } from 'lucide-react';
import { toast } from 'sonner';


export default function Marketplace() {
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [protocols, setProtocols] = useState([]);
  const [myShared, setMyShared] = useState([]);
  const [myProtocols, setMyProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('browse');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [shareDescription, setShareDescription] = useState('');

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [marketplaceRes, myProtocolsRes] = await Promise.all([
        fetch(`${API}/marketplace/protocols`, { credentials: 'include' }),
        fetch(`${API}/protocols`, { credentials: 'include' })
      ]);

      if (marketplaceRes.ok) {
        const data = await marketplaceRes.json();
        setProtocols(data.protocols || []);
        setMyShared(data.my_shared || []);
      }
      if (myProtocolsRes.ok) {
        setMyProtocols(await myProtocolsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (marketplaceId) => {
    try {
      const response = await fetch(`${API}/marketplace/protocols/${marketplaceId}/import`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Protocol imported to your library!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import protocol');
      }
    } catch (error) {
      toast.error('Failed to import protocol');
    }
  };

  const handleShare = async () => {
    if (!selectedProtocol) return;

    try {
      const response = await fetch(`${API}/marketplace/protocols`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          protocol_id: selectedProtocol.protocol_id,
          description: shareDescription
        })
      });

      if (response.ok) {
        toast.success('Protocol shared to marketplace!');
        setShowShareDialog(false);
        setSelectedProtocol(null);
        setShareDescription('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to share protocol');
      }
    } catch (error) {
      toast.error('Failed to share protocol');
    }
  };

  const handleRate = async (marketplaceId, rating) => {
    try {
      const response = await fetch(`${API}/marketplace/protocols/${marketplaceId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rating })
      });

      if (response.ok) {
        toast.success('Rating submitted!');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to submit rating');
    }
  };

  const filteredProtocols = protocols.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const trendingProtocols = [...protocols].sort((a, b) => (b.download_count || 0) - (a.download_count || 0)).slice(0, 6);
  const newestProtocols = [...protocols].sort((a, b) => new Date(b.shared_at) - new Date(a.shared_at)).slice(0, 6);
  const topRatedProtocols = [...protocols].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0)).slice(0, 6);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="marketplace-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <Store className="h-10 w-10 text-primary" />
            {t('marketplace.title')}
          </h1>
          <p className="text-muted-foreground">{t('marketplace.subtitle')}</p>
        </div>

        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogTrigger asChild>
            <Button data-testid="share-protocol-button">
              <Share2 className="h-4 w-4 mr-2" />
              {t('marketplace.shareYours')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Protocol to Marketplace</DialogTitle>
              <DialogDescription>
                Share your protocol with the community. Others can import and use it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Protocol</Label>
                <div className="grid gap-2 mt-2 max-h-48 overflow-y-auto">
                  {myProtocols.filter(p => !myShared.some(s => s.original_protocol_id === p.protocol_id)).map(protocol => (
                    <button
                      key={protocol.protocol_id}
                      onClick={() => setSelectedProtocol(protocol)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedProtocol?.protocol_id === protocol.protocol_id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-medium">{protocol.name}</p>
                      <p className="text-xs text-muted-foreground">{protocol.peptides?.length || 0} peptides</p>
                    </button>
                  ))}
                </div>
              </div>
              {selectedProtocol && (
                <div>
                  <Label>Description for Community</Label>
                  <Textarea
                    value={shareDescription}
                    onChange={(e) => setShareDescription(e.target.value)}
                    placeholder="Describe what this protocol is good for, any tips, etc."
                    rows={3}
                  />
                </div>
              )}
              <Button onClick={handleShare} className="w-full" disabled={!selectedProtocol}>
                <Share2 className="h-4 w-4 mr-2" />
                Share Protocol
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search protocols..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-lg grid-cols-4">
          <TabsTrigger value="browse">{t('marketplace.browse')}</TabsTrigger>
          <TabsTrigger value="trending">{t('marketplace.trending')}</TabsTrigger>
          <TabsTrigger value="newest">{t('marketplace.newest')}</TabsTrigger>
          <TabsTrigger value="my-shared">{t('marketplace.myShared')}</TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProtocols.length === 0 ? (
              <Card className="col-span-full p-12 text-center glass-card">
                <Store className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-semibold mb-2">No Protocols Found</h2>
                <p className="text-muted-foreground">
                  Be the first to share a protocol with the community!
                </p>
              </Card>
            ) : (
              filteredProtocols.map(protocol => (
                <ProtocolCard
                  key={protocol.marketplace_id}
                  protocol={protocol}
                  onImport={handleImport}
                  onRate={handleRate}
                  isPremium={isPremium}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="trending">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trendingProtocols.map(protocol => (
              <ProtocolCard
                key={protocol.marketplace_id}
                protocol={protocol}
                onImport={handleImport}
                onRate={handleRate}
                isPremium={isPremium}
                badge={<TrendingUp className="h-4 w-4 text-success" />}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="newest">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {newestProtocols.map(protocol => (
              <ProtocolCard
                key={protocol.marketplace_id}
                protocol={protocol}
                onImport={handleImport}
                onRate={handleRate}
                isPremium={isPremium}
                badge={<Clock className="h-4 w-4 text-info" />}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="my-shared">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myShared.length === 0 ? (
              <Card className="col-span-full p-12 text-center glass-card">
                <Share2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-semibold mb-2">No Shared Protocols</h2>
                <p className="text-muted-foreground mb-6">
                  Share your protocols to help other researchers!
                </p>
                <Button onClick={() => setShowShareDialog(true)}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Your First Protocol
                </Button>
              </Card>
            ) : (
              myShared.map(protocol => (
                <ProtocolCard
                  key={protocol.marketplace_id}
                  protocol={protocol}
                  onImport={handleImport}
                  onRate={handleRate}
                  isPremium={isPremium}
                  isOwner
                />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProtocolCard({ protocol, onImport, onRate, isPremium, badge, isOwner }) {
  const { t } = useTranslation();
  const [hoveredRating, setHoveredRating] = useState(0);

  return (
    <Card className="p-5 glass-card hover:border-primary/50 transition-all" data-testid={`marketplace-protocol-${protocol.marketplace_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{protocol.name}</h3>
            {badge}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('marketplace.by')} {protocol.author_name || 'Anonymous'}
          </p>
        </div>
        {isOwner && (
          <span className="px-2 py-1 rounded text-xs bg-primary/20 text-primary">
            Your Protocol
          </span>
        )}
      </div>

      {protocol.description && (
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {protocol.description}
        </p>
      )}

      <div className="space-y-1 mb-4">
        {protocol.peptides?.slice(0, 3).map((p, i) => (
          <p key={i} className="text-xs text-muted-foreground">
            • {p.peptide_name}: {p.dose_amount} {p.dose_unit}
          </p>
        ))}
        {protocol.peptides?.length > 3 && (
          <p className="text-xs text-muted-foreground">
            + {protocol.peptides.length - 3} more...
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Download className="h-3 w-3" />
          {protocol.download_count || 0}
        </span>
        <span className="flex items-center gap-1">
          <Star className="h-3 w-3 text-yellow-500" />
          {(protocol.average_rating || 0).toFixed(1)}
        </span>
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {protocol.rating_count || 0}
        </span>
      </div>

      {/* Rating */}
      {!isOwner && (
        <div className="flex items-center gap-1 mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => onRate(protocol.marketplace_id, star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="p-0.5"
            >
              <Star
                className={`h-4 w-4 transition-colors ${
                  star <= (hoveredRating || protocol.user_rating || 0)
                    ? 'text-yellow-500 fill-yellow-500'
                    : 'text-muted-foreground'
                }`}
              />
            </button>
          ))}
        </div>
      )}

      {/* Actions */}
      {!isOwner && (
        <Button
          onClick={() => onImport(protocol.marketplace_id)}
          className="w-full"
          variant={protocol.already_imported ? "outline" : "default"}
          disabled={protocol.already_imported}
        >
          {protocol.already_imported ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Already Imported
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Import Protocol
            </>
          )}
        </Button>
      )}

      {isOwner && (
        <div className="text-center text-xs text-muted-foreground">
          Shared {new Date(protocol.shared_at).toLocaleDateString()}
        </div>
      )}
    </Card>
  );
}
